import {Icon, TextInput, Tooltip} from '@/components/atoms';
import {defaultTo} from 'lodash';
import React, {useCallback, useEffect, useState} from 'react';
import './text-input.scss';
import useTextInputValidation from './useTextInputValidation-hook';
import {useSuffixPosition, SUFFIX_CONSTANTS} from './useSuffixPosition-hook';
import {useNumberKeyDown} from './useNumberKeyDown-hook';
import PropTypes from 'prop-types';
/**
 * TextInputField component renders a text input field with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the input field.
 * @param {function} props.updateValue - Function to update the value of the input field.
 * @param {string} props.label - The label for the input field.
 * @param {Object} props.schema - The schema defining the input field type and validation.
 * @param {Object} props.uiSchema - The UI schema defining additional UI properties.
 * @param {boolean} [props.required=false] - Whether the input field is required.
 * @param {boolean} [props.disabled=false] - Whether the input field is disabled.
 * @param {boolean} props.visible - Whether the input field is visible.
 * @param {string} [props.suffix] - Optional suffix to display after the input value (e.g., "x").
 *
 * @returns {JSX.Element|null} The rendered TextInputField component or null if not visible.
 */

const TextInputField = ({
  value,
  updateValue,
  label,
  schema,
  uiSchema,
  required = false,
  disabled = false,
  visible,
  suffix
}) => {
  const {onChange} = useTextInputValidation(label, uiSchema?.validationRules);
  const [error, setError] = useState();
  const [lastValidValue, setLastValidValue] = useState(value);
  const [touched, setTouched] = useState(false);
  const {suffixLeft, hiddenSpanRef, updateSuffixPosition, shouldRenderSuffix} =
    useSuffixPosition(suffix);
  const handleKeyDown = useNumberKeyDown(schema);

  const clearValidationError = (val) => {
    const validationError = onChange(val);
    if (!validationError) {
      setError(undefined);
      setLastValidValue(val);
      updateValue(val, false);
    }
  };

  const handleValidationError = useCallback(
    (errorMessage, val) => {
      setError(errorMessage);
      updateValue(val, true);
    },
    [updateValue]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
  }, []);

  const handleOnChange = (e) => {
    let newValue = typeof e === 'string' ? e : e?.target?.value;

    if (schema?.type === 'number' && newValue) {
      newValue = String(newValue).replace(/,/g, '');

      const decimalMatch = newValue.match(/^\d*\.?\d{0,2}/);
      if (decimalMatch) {
        newValue = decimalMatch[0];
      }
    }

    const validationError = onChange(newValue);

    updateSuffixPosition(newValue);

    if (validationError) {
      handleValidationError(validationError, newValue);
    } else if (error || newValue !== lastValidValue) {
      clearValidationError(newValue);
    }
  };

  useEffect(() => {
    // validate default value and update suffix position
    // Only validate if the field has been touched or has a non-empty value
    if (touched || (value && value !== '')) {
      handleOnChange(value);
    }
    updateSuffixPosition(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, updateSuffixPosition, touched]);

  const getInputType = (schema) => {
    return schema?.type === 'integer' ? 'number' : schema?.type || 'text';
  };

  const disableButton = disabled || uiSchema?.readOnly ? 'disable-button' : '';

  return visible ? (
    <>
      <div
        className={`input-text-wrapper ${suffix ? 'input-with-suffix' : ''} ${uiSchema?.className || ''}`}>
        <div className='input-with-suffix-container'>
          <TextInput
            required={required}
            className={`${uiSchema?.className ?? ''} ${disableButton} inputType-${getInputType(schema)}`}
            disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
            maxLength={uiSchema?.maxLength ?? undefined}
            type={getInputType(schema)}
            error={touched ? error : undefined}
            label={label}
            onChange={handleOnChange}
            onBlur={handleBlur}
            isDark={uiSchema?.isDark || false}
            value={defaultTo(!value ? '' : value, undefined)}
            placeholderText={uiSchema?.placeholder ?? ''}
            helperText={uiSchema?.helperText}
            handleKeyDown={handleKeyDown}
          />
          {suffix && (
            <>
              {/* Hidden span to measure text width */}
              <span
                ref={hiddenSpanRef}
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  whiteSpace: 'pre',
                  fontSize: '1rem',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontWeight: 'normal',
                  letterSpacing: 'normal',
                  lineHeight: 'normal',
                  textAlign: 'left',
                  left: `${SUFFIX_CONSTANTS.INPUT_PADDING_LEFT}px`,
                  top: 0,
                  zIndex: -1,
                  border: 'none',
                  padding: 0,
                  margin: 0
                }}
              />
              {/* Suffix positioned after text - only show when there's text */}
              {shouldRenderSuffix(value) && (
                <span
                  className='input-suffix-dynamic'
                  style={{
                    position: 'absolute',
                    left: `${suffixLeft + SUFFIX_CONSTANTS.SUFFIX_FINE_TUNE}px`,
                    bottom: `${SUFFIX_CONSTANTS.SUFFIX_BOTTOM_POSITION}px`,
                    color: 'var(--color_FFFFFF)',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    fontSize: '1rem',
                    zIndex: 10
                  }}>
                  {suffix}
                </span>
              )}
            </>
          )}
        </div>
        {uiSchema?.tooltipText && uiSchema?.tooltipText?.length > 0 && (
          <div className='tooltip-text'>
            <Tooltip text={uiSchema.tooltipText}>
              <Icon alt='' icon='info' size='small' />
            </Tooltip>
          </div>
        )}
      </div>

      {!error && uiSchema.helpText && (
        <div className='input-help-text items-center'>
          <Tooltip text={uiSchema.helpText} className='help-tooltip'>
            <Icon icon='info' size='small' />
          </Tooltip>
          {uiSchema.helpText ? uiSchema.helpText : 'Help'}
        </div>
      )}
    </>
  ) : null;
};

TextInputField.propTypes = {
  value: PropTypes.string,
  updateValue: PropTypes.func.isRequired,
  label: PropTypes.string,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
  suffix: PropTypes.string
};

export default React.memo(TextInputField);
